import {
  collection,
  DocumentData,
  onSnapshot,
  query,
  QueryDocumentSnapshot,
  Unsubscribe,
  where,
} from "firebase/firestore";
import { localProjects, Project } from "../data/projects";
import { firestoreDb, isFirebaseConfigured } from "../lib/firebase";

export type ProjectSource = "firestore" | "local";

type ProjectSubscriber = (projects: Project[], source: ProjectSource) => void;
type ProjectErrorHandler = (error: Error) => void;

const PROJECTS_COLLECTION = "projects";

function readString(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function readStringArray(value: unknown) {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function readNumber(value: unknown, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function normalizeProjectImages(images: string[]) {
  if (images.length === 0) {
    return [];
  }

  return [images[0], images[1] || images[0], images[2] || images[1] || images[0]];
}

export function mapProjectDocument(
  snapshot: QueryDocumentSnapshot<DocumentData>,
  includeUnpublished = false,
): Project | null {
  const data = snapshot.data();

  if (!includeUnpublished && data.published === false) {
    return null;
  }

  const images = normalizeProjectImages(readStringArray(data.images));
  const name = readString(data.name);
  const description = readString(data.description);
  const category = readString(data.category);
  const url = readString(data.url);

  if (!name || !description || !category || !url || images.length === 0) {
    return null;
  }

  return {
    category,
    description,
    features: readStringArray(data.features),
    id: snapshot.id,
    images,
    name,
    order: readNumber(data.order, Number.MAX_SAFE_INTEGER),
    published: data.published !== false,
    result: readString(data.result),
    stack: readStringArray(data.stack),
    url,
  };
}

export function sortProjects(projects: Project[]) {
  return [...projects].sort((first, second) => {
    if (first.order !== second.order) {
      return first.order - second.order;
    }

    return first.name.localeCompare(second.name);
  });
}

export function subscribeToProjects(
  onProjects: ProjectSubscriber,
  onError?: ProjectErrorHandler,
): Unsubscribe {
  if (!isFirebaseConfigured || !firestoreDb) {
    onProjects(localProjects, "local");
    return () => undefined;
  }

  return onSnapshot(
    query(
      collection(firestoreDb, PROJECTS_COLLECTION),
      where("published", "==", true),
    ),
    (snapshot) => {
      const firestoreProjects = sortProjects(
        snapshot.docs
          .map((document) => mapProjectDocument(document))
          .filter((project): project is Project => Boolean(project)),
      );

      if (firestoreProjects.length === 0) {
        onProjects(localProjects, "local");
        return;
      }

      onProjects(firestoreProjects, "firestore");
    },
    (error) => {
      console.warn(
        "Could not load projects from Firestore. Showing local project data instead.",
        error,
      );
      onError?.(error);
      onProjects(localProjects, "local");
    },
  );
}
